import React, { useState, useContext, useEffect } from "react";
import { navigate, Link } from "gatsby";
import { toast } from "react-toastify";
import Loading from "../../components/Loading";
import client from "../../apollo/client";
import { AuthUserContext } from "../../context/AuthUser";
import { apiBase } from "../../services/appenv";
import { getCurrentUser } from "../../services/api";

const LoginOTP = () => {
    const { setAuthUser } = useContext(AuthUserContext);

    const [otpEmail, setOtpEmail] = useState("");
    const [otpCode, setOtpCode] = useState("");
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [otpCountdown, setOtpCountdown] = useState(0);

    const getLocalStorage = (key) => {
        if (typeof window !== "undefined") {
            return localStorage.getItem(key);
        }
        return null;
    };

    const removeLocalStorage = (key) => {
        if (typeof window !== "undefined") {
            localStorage.removeItem(key);
        }
    };

    const clearUserSession = () => {
        removeLocalStorage("token");
        setAuthUser({ logged: false, user: {} });
        client.clearStore();
    };

    useEffect(() => {
        let interval = null;
        if (otpCountdown > 0) {
            interval = setInterval(() => {
                setOtpCountdown(otpCountdown - 1);
            }, 1000);
        } else if (otpCountdown === 0) {
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [otpCountdown]);

    useEffect(() => {
        const token = getLocalStorage("token");

        if (token) {
            navigate("/home");
        } else {
            client.clearStore();
            removeLocalStorage("token");
            navigate("/login");
        }
    }, [navigate, client]);

    const handleSendOTP = async (e) => {
        e.preventDefault();

        if (!otpEmail.trim()) {
            toast.error("Please enter a valid email address");
            return;
        }

        setOtpLoading(true);

        try {
            const response = await fetch(`${apiBase}/api/auth/send-otp`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({ email: otpEmail }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `Error ${response.status}`);
            }

            setOtpSent(true);
            setOtpCountdown(60);
            toast.success("Verification code sent to your email!");

        } catch (error) {
            toast.error("The user is not registered");
        } finally {
            setOtpLoading(false);
        }
    };

    const handleVerifyOTP = async (e) => {
        e.preventDefault();

        if (!otpCode.trim() || otpCode.length !== 6) {
            toast.error("Please enter a valid 6-digit code");
            return;
        }

        setOtpLoading(true);

        try {
            const response = await fetch(`${apiBase}/api/auth/verify-otp`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    email: otpEmail,
                    otp: otpCode
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `Error ${response.status}`);
            }

            if (data.user && data.user.blocked) {
                clearUserSession();
                toast.error("Account not yet activated.");
                return;
            }

            localStorage.setItem("token", data.jwt);

            try {
                const userDetails = await getCurrentUser(data.jwt);
                const { role, typecustomer, user_type } = userDetails;

                if (userDetails.blocked) {
                    clearUserSession();
                    toast.error("Account not yet activated.");
                    return;
                }

                if (role?.id) {
                    navigate(role.id === 4 ? "/orders" : "/home");

                    setAuthUser({
                        logged: true,
                        typeCustomer: typecustomer ? typecustomer : "",
                        role: role.id,
                        type: user_type ? user_type?.name : "",
                        user: { ...data.user, confirmed: typecustomer ? typecustomer : "" },
                    });

                    toast.success("Login successful!");
                } else {
                    clearUserSession();
                    toast.error("The user does not have access");
                }
            } catch (error) {
                clearUserSession();
                toast.error("Failed to fetch user details");
            }

        } catch (error) {
            if (error.message === "[object Object]") {
                return toast.error("Too many attempts")
            }
            toast.error("Invalid verification code" || "Code not registered");
        } finally {
            setOtpLoading(false);
        }
    };

    const resetOtpFlow = () => {
        setOtpSent(false);
        setOtpCode("");
        setOtpCountdown(0);
    };

    return (
        <>
            {otpLoading && <Loading />}

            <section className="container container-card-login">
                <h2>Login</h2>

                <div className="container-card-login__content">
                    <h3 style={{
                        textAlign: "center",
                        fontWeight: "400",
                        marginBlock: "20px",
                    }}>
                        Log in with Email Code
                    </h3>

                    {!otpSent ? (
                        <form onSubmit={handleSendOTP} noValidate>
                            <div className="container-card-login__input">
                                <input
                                    type="email"
                                    name="otpEmail"
                                    placeholder="E-mail address"
                                    value={otpEmail}
                                    onChange={(e) => setOtpEmail(e.target.value)}
                                    className="u-width-100"
                                    style={{
                                        height: "44px",
                                        border: "1px solid black",
                                    }}
                                    required
                                    disabled={otpLoading}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={otpLoading}
                                className="btn btn-primary"
                                style={{ margin: "auto", width: "250px" }}
                            >
                                {otpLoading ? (
                                    <div
                                        className="loading"
                                        style={{ width: "30px", height: "30px" }}
                                    ></div>
                                ) : (
                                    "Send Verification Code"
                                )}
                            </button>
                        </form>
                    ) : (
                        <>
                            <div style={{
                                textAlign: "center",
                                marginBottom: "20px",
                                color: "#666",
                                fontSize: "14px"
                            }}>
                                We sent a 6-digit code to:<br />
                                <strong>{otpEmail}</strong>
                            </div>

                            <form onSubmit={handleVerifyOTP} noValidate>
                                <div className="container-card-login__input">
                                    <input
                                        type="text"
                                        name="otpCode"
                                        placeholder="Enter 6-digit code"
                                        value={otpCode}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                            setOtpCode(value);
                                        }}
                                        className="u-width-100"
                                        style={{
                                            height: "44px",
                                            border: "1px solid black",
                                            textAlign: "center",
                                            fontSize: "22px",
                                            letterSpacing: "4px"
                                        }}
                                        maxLength="6"
                                        required
                                        disabled={otpLoading}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={otpLoading || otpCode.length !== 6}
                                    className="btn btn-primary"
                                    style={{
                                        margin: "auto",
                                        width: "250px",
                                        opacity: otpCode.length !== 6 ? 0.6 : 1
                                    }}
                                >
                                    {otpLoading ? (
                                        <div
                                            className="loading"
                                            style={{ width: "30px", height: "30px" }}
                                        ></div>
                                    ) : (
                                        "Verify Code"
                                    )}
                                </button>
                            </form>

                            <div style={{
                                textAlign: "center",
                                marginTop: "20px",
                                fontSize: "14px"
                            }}>
                                <button
                                    type="button"
                                    onClick={resetOtpFlow}
                                    disabled={otpLoading}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        color: "black",
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        marginRight: "20px",
                                        fontSize: "14px"
                                    }}
                                >
                                    Change email
                                </button>

                                {otpCountdown > 0 ? (
                                    <span style={{ color: "#666" }}>
                                        Resend code in {otpCountdown}s
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleSendOTP}
                                        disabled={otpLoading}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            color: "black",
                                            textDecoration: "underline",
                                            cursor: "pointer",
                                            fontSize: "14px"
                                        }}
                                    >
                                        Resend code
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    <p style={{ marginBottom: "0", marginTop: "1.5em", color: "black" }}>
                        Please agree to the{" "}
                        <Link
                            to="/terms-and-conditions"
                            style={{ textDecoration: "underline", color: "black" }}
                        >
                            terms of service
                        </Link>{" "}
                        and proceed.
                    </p>

                    <p style={{ marginBottom: "10px", marginTop: "1em", color: "black" }}>
                        No account?{" "}
                        <Link
                            to="/register-new"
                            className=""
                            style={{ textDecoration: "underline", color: "black" }}
                        >
                            Pre-register
                        </Link>{" "}
                        now.
                    </p>
                </div>
            </section>
        </>
    );
};

export default LoginOTP;